import { useCallback, useContext, useEffect, useRef } from 'react';

import * as Sentry from '@sentry/react';
import { capitalize, getStorageValue, setPhaseHash } from 'App/Helpers';
import { ConsentContext } from 'App/Providers/games/ConsentProvider';
import { PwaContext } from 'App/Services/Pwa';

import { sendPushGA } from '../helpers';
import { useRequestPushPermission } from '../OneSignal/useRequestPushPermission';
import { PushContext } from '../PushProvider';

import { usePushSubscriptionHandleTime } from './usePushSubscriptionHandleTime';

/**
 * Drives the PushPhase screen: view counting, page-show analytics, the native
 * permission prompt, and the hand-off to the next phase. Consumers keep only
 * their own markup.
 *
 * `updatePhase` and `setShowContent` come from the LP because the phase machine
 * lives in each game's own AppProvider.
 *
 * @param {Object} params
 * @param {string} params.storageKey - Base storage namespace for push values.
 * @param {(phase: string) => void} params.updatePhase - LP phase setter.
 * @param {(show: boolean) => void} params.setShowContent - Toggles the LP content gate.
 * @param {Function} params.sendGameDesignEvent - Deferred GameAnalytics design event.
 * @param {Function} params.sendGoogleBasicEvent - Deferred Google Analytics basic event.
 * @returns {{
 *   showLoader: boolean,
 *   disabled: boolean,
 *   buttonClickHandler: (buttonName: 'allow' | 'continue') => Promise<void>,
 * }}
 */
export const usePushPhaseFlow = ({
    storageKey,
    updatePhase,
    setShowContent,
    sendGameDesignEvent,
    sendGoogleBasicEvent,
}) => {
    const { functionalConsent } = useContext(ConsentContext);
    const { updatePushStatus, updatePushPhaseViews, pushServiceReady } =
        useContext(PushContext);
    const requestPermission = useRequestPushPermission();
    const { showPwaPhase } = useContext(PwaContext);

    const { showLoader, setShowLoader, markEndAndSend } =
        usePushSubscriptionHandleTime(storageKey);
    const allowInFlightRef = useRef(false);

    const leavePhase = useCallback(() => {
        setShowContent(false);
        updatePhase(showPwaPhase ? 'pwa' : 'game');
    }, [setShowContent, updatePhase, showPwaPhase]);

    useEffect(() => {
        // guard for certain mobile devices that silently grant push permissions upon PWA installation
        if (Notification.permission === 'granted') {
            updatePushStatus('granted');

            const hash = getStorageValue(storageKey, 'app.hash');

            if (hash) {
                Sentry.captureEvent({
                    message:
                        'The user has a “hash” on PWA load and apparently did not grant push permission.',
                    level: 'info',
                });
            }

            leavePhase();
            return;
        }

        setPhaseHash('2');
        updatePushPhaseViews();
        sendGameDesignEvent('Push:PageShow');
        sendGoogleBasicEvent('push', 'push_page_show');
    }, []);

    const buttonClickHandler = async (buttonName) => {
        sendGameDesignEvent(`Push:${capitalize(buttonName)}`);
        sendGoogleBasicEvent('push', `push_${buttonName}`);

        if (buttonName === 'allow') {
            if (allowInFlightRef.current) return;

            allowInFlightRef.current = true;
            setShowLoader(true);

            const permission = await requestPermission();

            markEndAndSend();

            if (permission)
                sendPushGA({
                    callSource: 'lp',
                    permission,
                    sendGameDesignEvent,
                    sendGoogleBasicEvent,
                });

            setShowLoader(false);
            leavePhase();
        } else if (buttonName === 'continue') {
            leavePhase();
        }
    };

    return {
        showLoader,
        disabled: !pushServiceReady || !functionalConsent,
        buttonClickHandler,
    };
};
